import React from "react"

import Icon from "@ng-mw/reol/components/Icon"
import Switch from "@ng-mw/reol/components/Switch"
import checkmarkBoldIcon from "@resources/icons/checkmarkBold"
import DinnerSuggestionsSelectionCard from "./DinnerSuggestionsSelectionCard"
import {
    dinnerSuggestionsImportantToMeOptions,
    dinnerSuggestionsMoreOfOptions,
} from "@modules/recipes/preferences/options"
import {
    DinnerSuggestionsImportantToMeOption,
    DinnerSuggestionsImportantToMeOptionId,
    DinnerSuggestionsMoreOfOption,
    DinnerSuggestionsMoreOfOptionId,
} from "../preferences/types"

interface MoreOfSelectionStepProps {
    description: string
    options: DinnerSuggestionsMoreOfOption[]
    selectedValues: DinnerSuggestionsMoreOfOptionId[]
    onToggle: (value: DinnerSuggestionsMoreOfOptionId) => void
}

interface ImportantSelectionStepProps {
    description: string
    options: DinnerSuggestionsImportantToMeOption[]
    selectedValues: DinnerSuggestionsImportantToMeOptionId[]
    onToggle: (value: DinnerSuggestionsImportantToMeOptionId) => void
}

interface ConsentStepProps {
    className?: string
    description?: string
    isChecked: boolean
    isDisabled: boolean
    onToggle: () => void
}

const DinnerSuggestionsMoreOfSelectionStep = ({
    description,
    options,
    selectedValues,
    onToggle,
}: MoreOfSelectionStepProps) => {
    return (
        <>
            <p className="dinner-suggestions-popup__text dinner-suggestions-popup__option-step-text">
                {description}
            </p>
            <ul className="dinner-suggestions-popup__option-list">
                {options.map(option => {
                    const isSelected = selectedValues.includes(option.id)

                    return (
                        <li
                            className="dinner-suggestions-popup__option-item"
                            key={option.id}
                        >
                            <DinnerSuggestionsSelectionCard
                                option={option}
                                isSelected={isSelected}
                                onClick={() => onToggle(option.id)}
                            />
                        </li>
                    )
                })}
            </ul>
        </>
    )
}

const DinnerSuggestionsImportantSelectionStep = ({
    description,
    options,
    selectedValues,
    onToggle,
}: ImportantSelectionStepProps) => {
    return (
        <>
            <p className="dinner-suggestions-popup__text dinner-suggestions-popup__option-step-text">
                {description}
            </p>
            <ul className="dinner-suggestions-popup__option-list">
                {options.map(option => {
                    const isSelected = selectedValues.includes(option.id)

                    return (
                        <li
                            className="dinner-suggestions-popup__option-item"
                            key={option.id}
                        >
                            <button
                                type="button"
                                className="dinner-suggestions-popup__option-card"
                                aria-pressed={isSelected}
                                onClick={() => onToggle(option.id)}
                            >
                                <span
                                    className={
                                        isSelected
                                            ? "dinner-suggestions-popup__option-check dinner-suggestions-popup__option-check--selected"
                                            : "dinner-suggestions-popup__option-check"
                                    }
                                    aria-hidden="true"
                                >
                                    {isSelected ? (
                                        <Icon
                                            icon={checkmarkBoldIcon}
                                            size={{ width: "16px", height: "16px" }}
                                        />
                                    ) : null}
                                </span>
                                <span className="dinner-suggestions-popup__option-copy">
                                    <span className="dinner-suggestions-popup__option-title">
                                        {option.label}
                                    </span>
                                    <span className="dinner-suggestions-popup__option-description">
                                        {option.description}
                                    </span>
                                </span>
                            </button>
                        </li>
                    )
                })}
            </ul>
        </>
    )
}

export const DinnerSuggestionsIntroStep = ({ isInfoOnlyFlow }: { isInfoOnlyFlow: boolean }) => {
    return (
        <>
            <h2 className="dinner-suggestions-popup__title">Din middag</h2>
            {isInfoOnlyFlow ? (
                <>
                    <p className="dinner-suggestions-popup__text">
                        Her finner du middagsforslag vi tror passer deg, basert på dine tidligere
                        kjøp og preferanser du har valgt.
                    </p>
                    <p className="dinner-suggestions-popup__text">
                        Dine valgte preferanser hjelper oss å forstå hva du vil spise mer av.
                        Samtidig bruker vi dine tidligere kjøp for å kunne gi deg enda mer relevante
                        anbefalinger. Du kan når som helst endre middagspreferansene dine ved å
                        klikke på tannhjul-ikonet.
                    </p>
                    <p className="dinner-suggestions-popup__text">
                        Forslagene blir mer relevante ettersom vi blir bedre kjent med deg.
                    </p>
                    <p className="dinner-suggestions-popup__text">Vel bekomme!</p>
                </>
            ) : (
                <>
                    <p className="dinner-suggestions-popup__text">
                        Velkommen til en enklere og bedre middagshverdag!
                    </p>
                    <p className="dinner-suggestions-popup__text">
                        Velg dine middagspreferanser og få relevante oppskrifter som vi tror passer
                        deg, basert på valgene du tar og dine tidligere kjøp.
                    </p>
                </>
            )}
        </>
    )
}

export const DinnerSuggestionsMoreOfStep = ({
    selectedValues,
    onToggle,
}: Omit<MoreOfSelectionStepProps, "description" | "options">) => {
    return (
        <DinnerSuggestionsMoreOfSelectionStep
            description="Velg opptil 4 proteiner:"
            options={dinnerSuggestionsMoreOfOptions}
            selectedValues={selectedValues}
            onToggle={onToggle}
        />
    )
}

export const DinnerSuggestionsImportantStep = ({
    selectedValues,
    onToggle,
}: Omit<ImportantSelectionStepProps, "description" | "options">) => {
    return (
        <DinnerSuggestionsImportantSelectionStep
            description="Velg opptil 4 egenskaper:"
            options={dinnerSuggestionsImportantToMeOptions}
            selectedValues={selectedValues}
            onToggle={onToggle}
        />
    )
}

export const DinnerSuggestionsConsentStep = ({
    className,
    description,
    isChecked,
    isDisabled,
    onToggle,
}: ConsentStepProps) => {
    return (
        <div className={className}>
            <p className="dinner-suggestions-popup__consent-lead">
                Jeg samtykker til at Trumf-medlemskapet mitt tilpasses meg, basert på analyser av
                mine personopplysninger, handler, bruk av tjenester og annen informasjon Trumf har
                om meg.
            </p>

            {description ? (
                <p className="dinner-suggestions-popup__consent-paragraph">{description}</p>
            ) : null}

            <div className="dinner-suggestions-popup__consent-toggle-row">
                <label
                    className="dinner-suggestions-popup__consent-toggle-label"
                    htmlFor="dinner-suggestions-analytical-consent"
                >
                    Jeg samtykker til analytiske modeller
                </label>
                <Switch
                    className="dinner-suggestions-popup__consent-switch"
                    id="dinner-suggestions-analytical-consent"
                    name="dinner-suggestions-analytical-consent"
                    isChecked={isChecked}
                    isDisabled={isDisabled}
                    onChange={onToggle}
                />
            </div>

            <p className="dinner-suggestions-popup__consent-footnote">
                Du kan når som helst trekke tilbake samtykket.
            </p>
        </div>
    )
}
